import {useEffect, useMemo, useState} from 'react'
import mapboxgl from 'mapbox-gl'
import RouteMap from '@/components/map/RouteMap.jsx'
import {formatDHM} from '@/utils/dateUtils'

/**
 * A TRIP'S ROUTE — the static route map on ride details, the home card, the booking
 * banner and the processing step. Mapbox, via `RouteMap` (the same component the live
 * tracking map uses).
 *
 * Replaces `googleMapPolygone` (Google Maps), removed after its Polyline crashed the ride
 * page on re-render (`Cannot read properties of undefined (reading 'setAt')`). Same props,
 * so every call site swapped by import alone.
 *
 * Keeps the Google version's leg badges — time / distance / place at each stop, and the
 * whole trip's total over the destination — as Mapbox markers. `minimal` hides them (the
 * processing step's clean look).
 */

const ICON_SIZE = 32

function formatDuration(minutes) {
    return minutes ? formatDHM(minutes) : ''
}

function shortName(address) {
    if (!address) return ''
    return address.split(',')[0].trim()
}

const toPoint = (loc) => {
    const lat = parseFloat(loc?.lat)
    const lng = parseFloat(loc?.lng)
    return Number.isFinite(lat) && Number.isFinite(lng) ? {lat, lng} : null
}

/** The badge DOM — the Google version's markup and inline styles, unchanged. */
function badgeElement({duration, distance, name}) {
    const wrap = document.createElement('div')
    wrap.style.cssText = 'display:inline-flex;align-items:stretch;border-radius:10px;'
        + 'box-shadow:0 2px 8px rgba(0,0,0,0.18);background:#fff;overflow:hidden;pointer-events:none;'
        + 'white-space:nowrap;font-size:11px;line-height:1.3'

    const left = document.createElement('div')
    left.style.cssText = 'background:#1a1a1a;color:#fff;padding:5px 10px;display:flex;'
        + 'flex-direction:column;align-items:center;justify-content:center;gap:2px'
    const time = document.createElement('span')
    time.textContent = duration
    left.appendChild(time)
    if (distance) {
        const km = document.createElement('span')
        km.style.cssText = 'color:#8ffad7;font-weight:600'
        km.textContent = distance
        left.appendChild(km)
    }
    wrap.appendChild(left)

    if (name) {
        const right = document.createElement('div')
        right.style.cssText = 'background:#fff;color:#333;padding:5px 10px;display:flex;align-items:center;font-size:10px'
        right.textContent = name
        wrap.appendChild(right)
    }

    return wrap
}

export default function TripRouteMap({
    polyline, origin, destination, stopOvers, legs,
    className = '', animate = false, minimal = false, onIntroDone,
}) {
    const [map, setMap] = useState(null)

    // Badges: one per intermediate stop, plus the whole trip over the destination.
    const badges = useMemo(() => {
        if (minimal || !legs?.length) return []
        const out = []
        legs.slice(0, -1).forEach((leg) => {
            const at = toPoint(leg.end_location)
            if (!at) return
            out.push({
                at,
                offset: ICON_SIZE / 2 + 18,
                duration: formatDuration(leg.duration_minutes),
                distance: leg.distance_km ? `${leg.distance_km} km` : (leg.distance_text || ''),
                name: shortName(leg.end_location.address || leg.end_location.formatted_address || ''),
            })
        })
        const lastEnd = legs[legs.length - 1]?.end_location
        const end = toPoint(lastEnd) || toPoint(destination)
        if (end) {
            const minutes = legs.reduce((sum, leg) => sum + (leg.duration_minutes || 0), 0)
            const km = legs.reduce((sum, leg) => sum + (parseFloat(leg.distance_km) || 0), 0)
            out.push({
                at: end,
                offset: ICON_SIZE / 2 + 10,
                duration: formatDuration(minutes),
                distance: km > 0 ? `${km.toFixed(1)} km` : '',
                name: shortName(lastEnd?.address || lastEnd?.formatted_address || ''),
            })
        }
        return out.filter((b) => b.duration || b.distance || b.name)
    }, [legs, destination, minimal])

    useEffect(() => {
        if (!map || badges.length === 0) return undefined
        const markers = badges.map((b) => new mapboxgl.Marker({
            element: badgeElement(b),
            anchor: 'bottom',
            offset: [0, -b.offset],
        }).setLngLat([b.at.lng, b.at.lat]).addTo(map))
        return () => markers.forEach((m) => m.remove())
    }, [map, badges])

    return (
        <RouteMap
            polyline={polyline}
            origin={origin}
            destination={destination}
            stopOvers={stopOvers}
            legs={legs}
            animate={animate}
            onIntroDone={onIntroDone}
            onMapReady={setMap}
            recentreControl={false}
            indoorControl={false}
            className={className}
        />
    )
}
